import React from 'react'
import { formatAmount, maskAmount, formatRelativeTime } from '../utils/format'
import { StatusPill } from './StatusPill'
import { Badge } from '@/components/ui/badge'
import type { Estimate } from '../estimates/types'

interface QuoteListItemProps {
  estimate: Estimate & { proposerType?: 'TEAM' | 'INDIVIDUAL', proposerName?: string | null }
  isSelected: boolean
  isRead: boolean
  onClick: () => void
  compareSelected?: boolean
  compareDisabled?: boolean
  onToggleCompare?: (checked: boolean) => void
}

export const QuoteListItem: React.FC<QuoteListItemProps> = ({
  estimate,
  isSelected,
  isRead,
  onClick,
  compareSelected = false,
  compareDisabled = false,
  onToggleCompare,
}) => {
  const hasAccess = estimate.total_amount !== null && estimate.detail !== null
  const proposerType = estimate.proposerType || (estimate.teams && estimate.teams.length > 0 ? 'TEAM' : 'INDIVIDUAL')
  const proposerName = proposerType === 'TEAM'
    ? (estimate.teams?.[0]?.name || '팀')
    : (estimate.proposerName || '개인')
  const recentUpdateDate = estimate.updated_at || estimate.estimate_date || estimate.created_at
  const periodLabel = estimate.counsel?.period || estimate.counsel?.start_date || '납기 협의'
  const milestoneCount = estimate.milestone?.length ?? 0

  return (
    <div
      onClick={onClick}
      className={`
        rounded-lg border p-3 cursor-pointer transition-all relative
        ${isSelected
          ? 'border-l-4 border-l-blue-500 border-gray-200 bg-blue-50/80 shadow-sm'
          : !isRead
            ? 'border-l-4 border-l-blue-400 border-gray-200 bg-blue-50/50 hover:border-blue-300 hover:shadow-sm'
            : 'border-gray-200 bg-white hover:border-blue-200 hover:shadow-sm'
        }
      `}
    >
      {/* 상태 배지: 카드 오른쪽 상단 고정 */}
      <div className="absolute top-3 right-3 shrink-0">
        <StatusPill status={estimate.estimate_status} />
      </div>

      {/* 상단 좌측: 팀/개인 · 제출자명 (우측은 상태 배지라서 패딩) */}
      <div className="flex items-center gap-2 min-w-0 pr-20 mb-1.5">
        {onToggleCompare && (
          <input
            type="checkbox"
            checked={compareSelected}
            disabled={compareDisabled && !compareSelected}
            onChange={(e) => onToggleCompare(e.target.checked)}
            onClick={(e) => e.stopPropagation()}
            className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 shrink-0"
            aria-label="비교에 담기"
          />
        )}
        <Badge
          variant={proposerType === 'TEAM' ? 'default' : 'secondary'}
          className="rounded-full px-2 py-0.5 text-xs font-medium shrink-0"
        >
          {proposerType === 'TEAM' ? '팀' : '개인'}
        </Badge>
        <span className={`truncate text-sm ${!isRead ? 'font-bold text-gray-900' : 'font-semibold text-gray-900'}`}>
          {proposerName}
        </span>
        {!isRead && (
          <Badge variant="default" className="rounded-full px-2 py-0.5 text-xs font-bold bg-blue-600 text-white shrink-0">
            새
          </Badge>
        )}
      </div>

      {/* 제목 1줄 */}
      <h3 className={`text-sm line-clamp-1 mb-1 ${!isRead ? 'font-semibold text-gray-900' : 'font-medium text-gray-900'}`}>
        {estimate.counsel?.title || '제목 없음'}
      </h3>
      {/* 설명 1줄 말줄임 */}
      {estimate.counsel?.outline && (
        <p className="text-xs text-gray-600 line-clamp-1 mb-2 truncate">{estimate.counsel.outline}</p>
      )}

      {/* 하단: 좌측 금액만 강조, 우측 기간·마일스톤·최근업데이트 */}
      <div className="flex items-center justify-between gap-2 pt-2 border-t border-gray-100">
        <span className="text-sm font-bold text-gray-900">
          {hasAccess ? formatAmount(estimate.total_amount) : maskAmount()}
        </span>
        <span className="text-xs text-gray-500 truncate text-right">
          {periodLabel}
          {milestoneCount > 0 && ` · 마일스톤 ${milestoneCount}`}
          {recentUpdateDate && ` · ${formatRelativeTime(recentUpdateDate)}`}
        </span>
      </div>
    </div>
  )
}
